'use client';

import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField, TextAreaField, TextField } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/Feedback';
import { Icon } from '@/components/ui/Icon';
import { useLeadSubmission } from '@/hooks/useLeadSubmission';
import { hasErrors, isBlank, isEmail, messages, type ValidationErrors } from '@/lib/validation';

import { SubmissionError, SubmissionSuccess } from './FormFeedback';

interface PartnerFormValues {
  name: string;
  email: string;
  phone: string;
  platformName: string;
  platformType: string;
  website: string;
  message: string;
}

const PLATFORM_TYPES = [
  { value: 'affiliate', label: 'Affiliate platform' },
  { value: 'social-casino', label: 'Social casino / sweepstakes' },
  { value: 'social-media', label: 'Social media platform' },
  { value: 'igaming', label: 'iGaming operator' },
  { value: 'agency', label: 'Advertising agency' },
  { value: 'other', label: 'Something else' },
];

function validate(values: PartnerFormValues): ValidationErrors<PartnerFormValues> {
  return {
    name: isBlank(values.name) ? messages.required : undefined,
    email: isBlank(values.email) ? messages.required : isEmail(values.email) ? undefined : messages.email,
    platformName: isBlank(values.platformName) ? messages.required : undefined,
    platformType: isBlank(values.platformType) ? messages.required : undefined,
    message: isBlank(values.message)
      ? messages.required
      : values.message.trim().length < 20
        ? messages.tooShort(20)
        : undefined,
  };
}

/** Task 4: "Do you have a platform the LaunchPad could support? Tell us what you have." */
export function PartnerInquiryForm() {
  const [values, setValues] = useState<PartnerFormValues>({
    name: '',
    email: '',
    phone: '',
    platformName: '',
    platformType: '',
    website: '',
    message: '',
  });
  const [errors, setErrors] = useState<ValidationErrors<PartnerFormValues>>({});
  const submission = useLeadSubmission();

  const setValue = <K extends keyof PartnerFormValues>(key: K, value: PartnerFormValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) return;

    await submission.submit({
      type: 'partner',
      name: values.name,
      email: values.email,
      phone: values.phone,
      business: values.platformName,
      message: values.message,
      details: {
        platformType: PLATFORM_TYPES.find((type) => type.value === values.platformType)?.label ?? values.platformType,
        website: values.website,
      },
    });
  };

  if (submission.status === 'success') {
    return (
      <SubmissionSuccess
        title="Thanks — we got it"
        description="We will review your platform and come back to you with how the LaunchPad network can carry it, what the commercials look like and what we need from you to get started."
        onReset={submission.reset}
      />
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        <div className="grid gap-6 sm:grid-cols-2">
          <TextField
            label="Your name"
            required
            autoComplete="name"
            value={values.name}
            error={errors.name}
            onChange={(event) => setValue('name', event.target.value)}
          />
          <TextField
            label="Email"
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            value={values.email}
            error={errors.email}
            onChange={(event) => setValue('email', event.target.value)}
          />
          <TextField
            label="Phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            value={values.phone}
            onChange={(event) => setValue('phone', event.target.value)}
          />
          <TextField
            label="Platform name"
            required
            placeholder="e.g. Ruby Sweeps"
            value={values.platformName}
            error={errors.platformName}
            onChange={(event) => setValue('platformName', event.target.value)}
          />
          <SelectField
            label="What kind of platform is it?"
            required
            placeholder="Select a type"
            options={PLATFORM_TYPES}
            value={values.platformType}
            error={errors.platformType}
            onChange={(event) => setValue('platformType', event.target.value)}
          />
          <TextField
            label="Website"
            type="url"
            placeholder="https://"
            inputMode="url"
            value={values.website}
            onChange={(event) => setValue('website', event.target.value)}
          />
        </div>

        <TextAreaField
          label="Tell us what you have"
          required
          rows={6}
          hint="Player volume, the markets you operate in, your affiliate terms, and where you would want LaunchPads placed."
          value={values.message}
          error={errors.message}
          onChange={(event) => setValue('message', event.target.value)}
        />

        <SubmissionError submission={submission} />

        <Button type="submit" size="lg" disabled={submission.status === 'submitting'} className="self-start">
          {submission.status === 'submitting' ? (
            <>
              <Spinner /> Sending…
            </>
          ) : (
            <>
              Send my platform details
              <Icon name="arrow" className="h-5 w-5" />
            </>
          )}
        </Button>
      </form>
    </Card>
  );
}
